import { useCallback, useState } from 'react';

import { Box, Button, Col, Dialog, PressableBox, Row, Text } from '@opendoor/bricks-next';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import type { SavedSearchFieldsFragment } from '__generated__/athena';

import { useObservability } from '../../../helpers/observability';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { SAVED_SEARCHES_ENABLED } from './featureFlag';
import { RenameSavedSearchDialog } from './RenameSavedSearchDialog';
import { useSavedSearches } from './useSavedSearches';

export interface SavedSearchesDropdownProps {
  /**
   * Applies the selected saved search. The host page owns the marketplace
   * search state, so it decides whether to refetch results in place (same
   * market) or hard-navigate for fresh SSR data (different market / zone /
   * polygon).
   */
  onApplySavedSearch: (savedSearch: SavedSearchFieldsFragment) => void;
  /**
   * Optional size override for the trigger button. Defaults to "small" to
   * match `QuickFilters` controls.
   */
  size?: 'xsmall' | 'small' | 'medium';
}

export const SavedSearchesDropdown = ({
  onApplySavedSearch,
  size = 'small',
}: SavedSearchesDropdownProps) => {
  const { trackEvent } = useObservability();
  // Single source of truth for the list and its mutations. The rename/delete
  // dialogs run their mutation through this same instance (via the injected
  // `update`/`remove`) so the list refetches and updates here in place.
  const { savedSearches, update, remove } = useSavedSearches();

  const [isOpen, setIsOpen] = useState(false);
  const [pendingRename, setPendingRename] = useState<SavedSearchFieldsFragment | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedSearchFieldsFragment | null>(null);

  const handleSelect = useCallback(
    (savedSearch: SavedSearchFieldsFragment) => {
      trackEvent('cta-click', 'saved-search-selected', {
        savedSearchId: savedSearch.id,
        market: savedSearch.location.market,
      });
      // Delegate to the host page, which owns the marketplace search state and
      // decides whether to refetch results in place or hard-navigate. The
      // previous `router.push` only updated the URL — the long-lived page never
      // re-hydrated from the new params, so the result set never changed.
      onApplySavedSearch(savedSearch);
      setIsOpen(false);
    },
    [onApplySavedSearch, trackEvent],
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleOpenRename = useCallback((savedSearch: SavedSearchFieldsFragment) => {
    setPendingRename(savedSearch);
  }, []);

  const handleOpenDelete = useCallback((savedSearch: SavedSearchFieldsFragment) => {
    setPendingDelete(savedSearch);
  }, []);

  const handleCloseRename = useCallback(() => {
    setPendingRename(null);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const hasSavedSearches = savedSearches.length > 0;

  // Keep rendering while the menu is open even if the list transiently empties
  // (e.g. an auth re-evaluation), so a background re-render can't tear down the
  // open menu before the user has acted on it.
  if (!SAVED_SEARCHES_ENABLED || (!hasSavedSearches && !isOpen)) {
    return null;
  }

  const buttonLabel = `Saved searches (${savedSearches.length})`;

  return (
    <>
      {hasSavedSearches && (
        <Button
          analyticsName="saved-searches-dropdown-trigger"
          label={buttonLabel}
          onPress={handleOpen}
          size={size}
          variant="secondary"
        />
      )}
      <Dialog
        analyticsName="saved-searches-dropdown-dialog"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        dismissable
      >
        <Box width="100%" $largerThanSM={{ width: 420 }}>
          <Dialog.Content
            title="Your saved searches"
            description="Tap a search to switch between them, or rename and delete here."
          >
            <Col pt={4}>
              {savedSearches.map((savedSearch, index) => (
                <SavedSearchRow
                  key={savedSearch.id}
                  savedSearch={savedSearch}
                  isLast={index === savedSearches.length - 1}
                  onSelect={handleSelect}
                  onRename={handleOpenRename}
                  onDelete={handleOpenDelete}
                />
              ))}
            </Col>
          </Dialog.Content>
        </Box>
      </Dialog>
      {pendingRename && (
        <RenameSavedSearchDialog
          savedSearch={pendingRename}
          isOpen={pendingRename !== null}
          onClose={handleCloseRename}
          onRename={update}
        />
      )}
      {pendingDelete && (
        <ConfirmDeleteDialog
          savedSearch={pendingDelete}
          isOpen={pendingDelete !== null}
          onClose={handleCloseDelete}
          onConfirmDelete={remove}
        />
      )}
    </>
  );
};

interface SavedSearchRowProps {
  savedSearch: SavedSearchFieldsFragment;
  isLast: boolean;
  onSelect: (savedSearch: SavedSearchFieldsFragment) => void;
  onRename: (savedSearch: SavedSearchFieldsFragment) => void;
  onDelete: (savedSearch: SavedSearchFieldsFragment) => void;
}

const SavedSearchRow = ({
  savedSearch,
  isLast,
  onSelect,
  onRename,
  onDelete,
}: SavedSearchRowProps) => {
  const handleSelect = useCallback(() => {
    onSelect(savedSearch);
  }, [onSelect, savedSearch]);

  const handleRename = useCallback(() => {
    onRename(savedSearch);
  }, [onRename, savedSearch]);

  const handleDelete = useCallback(() => {
    onDelete(savedSearch);
  }, [onDelete, savedSearch]);

  // The name is the primary tap target (switch); rename/delete are compact,
  // secondary icon actions so the row stays scannable and the full name shows.
  return (
    <Row
      alignItems="center"
      gap={8}
      py={14}
      borderBottomWidth={isLast ? 0 : 1}
      borderBottomColor="$borderSecondary"
    >
      <PressableBox
        flex={1}
        analyticsName="saved-search-select"
        aria-label={`Switch to saved search ${savedSearch.name}`}
        onPress={handleSelect}
        py={4}
        pr={8}
        alignItems="flex-start"
      >
        <Text tag="p" typography="$bodyMedium" numberOfLines={2} textAlign="left">
          {savedSearch.name}
        </Text>
      </PressableBox>
      <Row alignItems="center" gap={2}>
        <PressableBox
          analyticsName="saved-search-rename"
          aria-label={`Rename ${savedSearch.name}`}
          onPress={handleRename}
          p={10}
          borderRadius={8}
          alignItems="center"
          justifyContent="center"
        >
          <IconPencil size={18} aria-hidden />
        </PressableBox>
        <PressableBox
          analyticsName="saved-search-delete"
          aria-label={`Delete ${savedSearch.name}`}
          onPress={handleDelete}
          p={10}
          borderRadius={8}
          alignItems="center"
          justifyContent="center"
        >
          <IconTrash size={18} aria-hidden />
        </PressableBox>
      </Row>
    </Row>
  );
};
